import Grid from '@material-ui/core/Grid';
import GridFormCheckbox from 'shared/components/GridFormCheckbox';
import GridFormField from 'shared/components/GridFormField';
import IconButton from '@material-ui/core/IconButton';
import React from 'react';
import removeHTML from 'shared/utils/removeHTML';
import Typography from '@material-ui/core/Typography';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import {
	ageInRange,
	emailValidation,
	maxNameLength,
	maxStrLength,
	numericValidation,
	required,
	settingValidation,
	isAfterDob,
	isValidDate,
	restrictedTextInputValidation,
	zipPostalValidation,
} from 'shared/utils/validations';
import {
	formatNumber,
	formatSsn,
	formatSsnHidden,
	normalizeSsn,
	normalizeSsnHidden,
	parseNumber,
	formatZipPostal,
	normalizeZipPostal,
} from 'shared/utils/formatting';
import { FormSection } from 'redux-form';
import InputAdornment from '@material-ui/core/InputAdornment';
import { MANDATORY, OPTIONAL } from 'redux/domain/Settings/constants';
import { PropTypes } from 'prop-types';
import { PERSONAL_INFORMATION, STATES, MARITAL_STATUS_OPTIONS } from './constants';
import exactStrLength from '../../shared/utils/validations/exactStrLength';

class PersonalInformationFields extends React.Component {
	constructor() {
		super();
		this.state = { showSsn: false };
		this.handleClickShowSsn = this.handleClickShowSsn.bind(this);
		this.formatSsnField = this.formatSsnField.bind(this);
		this.normalizeSsnField = this.normalizeSsnField.bind(this);
		this.renderSsnIcon = this.renderSsnIcon.bind(this);
		this.formatZipPostalField = this.formatZipPostalField.bind(this);
		this.normalizeZipPostalField = this.normalizeZipPostalField.bind(this);
	}

	handleClickShowSsn() {
		this.setState({ showSsn: !this.state.showSsn });
	}

	formatSsnField(value) {
		if (this.state.showSsn) {
			return formatSsn(value);
		}
		return formatSsnHidden(value);
	}

	normalizeSsnField(value, previousValue) {
		if (this.state.showSsn) {
			return normalizeSsn(value);
		}
		return normalizeSsnHidden(value, previousValue);
	}

	// eslint-disable-next-line class-methods-use-this
	formatZipPostalField(value) {
		return formatZipPostal(value);
	}

	// eslint-disable-next-line class-methods-use-this
	normalizeZipPostalField(value) {
		return normalizeZipPostal(value);
	}

	renderSsnIcon() {
		if (this.state.showSsn) {
			return <VisibilityOff />;
		}
		return <Visibility />;
	}

	render() {
		const { disableAll, settings, name } = this.props;
		const maxFirstNameLength = 20;
		const maxLastNameLength = 50;

		return (
			<FormSection name={name} style={{ width: '100%' }}>
				<Grid container spacing={5}>
					<GridFormField
						fieldSettings={{ fieldStatus: MANDATORY }}
						name="firstName"
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.FirstNameLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.FirstNameDescription}
						validation={[restrictedTextInputValidation, required, maxStrLength(maxFirstNameLength)]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={{ fieldStatus: MANDATORY }}
						name="lastName"
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.LastNameLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.LastNameDescription}
						validation={[restrictedTextInputValidation, required, maxStrLength(maxLastNameLength)]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={{ fieldStatus: MANDATORY }}
						name="address1"
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.Address1Label}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.Address1Description}
						validation={[required, maxStrLength(30)]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={{ fieldStatus: OPTIONAL }}
						name="address2"
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.Address2Label}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.Address2Description}
						validation={maxStrLength(30)}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={{ fieldStatus: MANDATORY }}
						name="city"
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.CityLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.CityDescription}
						validation={[required, maxStrLength(30)]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={{ fieldStatus: MANDATORY }}
						name="state"
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.StateLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.StateDescription}
						gridItemSize={{ xs: 8, md: 4 }}
						select
						menuOptions={STATES}
						validation={required}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={{ fieldStatus: MANDATORY }}
						name="zipCode"
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.PostalLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.PostalDescription}
						gridItemSize={{ xs: 4, md: 2 }}
						validation={[zipPostalValidation, required, maxStrLength(10)]}
						format={this.formatZipPostalField}
						normalize={this.normalizeZipPostalField}
						disabled={disableAll}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.SsnLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.SsnDescription}
						fieldSettings={settings.primary.socialSecurityNumber}
						name="ssn"
						validation={[
							settingValidation(settings.primary.socialSecurityNumber),
							exactStrLength(9),
						]}
						disabled={disableAll}
						format={this.formatSsnField}
						normalize={this.normalizeSsnField}
						endAdornment={
							<InputAdornment position="end">
								<IconButton
									onClick={this.handleClickShowSsn}
									onMouseDown={e => e.preventDefault()} // Don't change focused field when pressing button
								>
									{this.renderSsnIcon()}
								</IconButton>
							</InputAdornment>
						}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.DriversLicenseLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.DriversLicenseDescription}
						fieldSettings={settings.primary.driversLicenseNumber}
						name="driversLicense"
						validation={[
							settingValidation(settings.primary.driversLicenseNumber),
							maxStrLength(25),
						]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={settings.primary.driversLicenseState}
						name="driversLicenseState"
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.DriversLicenseStateLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.DriversLicenseStateDescription}
						select
						menuOptions={STATES}
						validation={settingValidation(settings.primary.driversLicenseState)}
						disabled={disableAll}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.DriversLicenseExpDateLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.DriversLicenseExpDateDescription}
						fieldSettings={settings.primary.driversLicenseExpDate}
						name="driversLicenseExpDate"
						type="date"
						inputProps={{
							style: { textTransform: 'uppercase', marginTop: '16px' },
							placeholder: 'MM/dd/yyyy',
						}}
						labelShrink
						validation={[
							settingValidation(settings.primary.driversLicenseExpDate),
							isValidDate,
							isAfterDob,
						]}
						disabled={disableAll}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.DateOfBirthLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.DateOfBirthDescription}
						fieldSettings={settings.primary.birthdate}
						name="dob"
						type="date"
						inputProps={{
							style: { textTransform: 'uppercase', marginTop: '16px' },
							placeholder: 'MM/dd/yyyy',
						}}
						labelShrink
						validation={[settingValidation(settings.primary.birthdate), ageInRange(18, 110)]}
						disabled={disableAll}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.MaritalStatusLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.MaritalStatusDescription}
						fieldSettings={settings.primary.maritalStatus}
						name="maritalStatus"
						select
						menuOptions={MARITAL_STATUS_OPTIONS}
						validation={settingValidation(settings.primary.maritalStatus)}
						disabled={disableAll}
					/>
					<GridFormCheckbox
						label={settings.fieldAndFooterSettings.ApplicationPageSettings.CheckingAccountLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.CheckingAccountDescription}
						fieldSettings={settings.primary.hasCheckingAccount}
						name="hasCheckingAccount"
						disabled={disableAll}
					/>
					<GridFormCheckbox
						label={settings.fieldAndFooterSettings.ApplicationPageSettings.SavingsAccountLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.SavingsAccountDescription}
						fieldSettings={settings.primary.hasSavingsAccount}
						name="hasSavingsAccount"
						disabled={disableAll}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.MothersMaidenNameLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.MothersMaidenNameDescription}
						fieldSettings={settings.primary.mothersMaidenName}
						name="mothersMaidenName"
						validation={[
							settingValidation(settings.primary.mothersMaidenName),
							maxStrLength(maxLastNameLength),
							maxNameLength,
							restrictedTextInputValidation,
						]}
						disabled={disableAll}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.EmailLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.EmailDescription}
						fieldSettings={settings.primary.emailAddress}
						name="emailAddress"
						validation={[
							settingValidation(settings.primary.emailAddress),
							emailValidation,
							maxStrLength(50),
						]}
						disabled={disableAll}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.RequestedAmountLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.RequestedAmountDescription}
						fieldSettings={settings.primary.requestedAmount}
						name="requestedAmount"
						validation={[
							settingValidation(settings.primary.requestedAmount),
							maxStrLength(6),
							numericValidation,
						]}
						format={formatNumber}
						parse={parseNumber}
						disabled={disableAll}
					/>
				</Grid>
				<Typography type="caption" component="div" style={{ paddingTop: '20px' }}>
					<div
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={{
							__html: removeHTML(settings.fieldAndFooterSettings.ApplicationPageSettings.PersonalInformationFooter),
						}}
					/>
				</Typography>
			</FormSection>
		);
	}
}

PersonalInformationFields.propTypes = {
	disableAll: PropTypes.bool,
	settings: PropTypes.shape({
		primary: PropTypes.objectOf(
			PropTypes.oneOfType([
				PropTypes.shape({
					fieldId: PropTypes.string,
					fieldStatus: PropTypes.string,
					fieldSubHeaderType: PropTypes.string,
				}),
				PropTypes.number,
			]),
		),
	}),
	name: PropTypes.string,
};

PersonalInformationFields.defaultProps = {
	disableAll: false,
	settings: {},
	name: PERSONAL_INFORMATION,
};

export default PersonalInformationFields;
